import useAuth from '../../../hooks/auth/useAuth';
import {groupKey, allocationMembers} from '../../../services/groups/groupModel';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {Link, useNavigate} from 'react-router-dom';
import {Alert, Button, Stack, Typography} from '@mui/material';
import {EditOutlined, PhotoCameraOutlined, ReceiptOutlined, LockOutlined, PeopleOutlineRounded} from '@mui/icons-material';
import {api} from '../../../services';
import {Group} from '../../../services/groups/groupModel';
import {ErrorResponse} from '../../../services/models/error-response';
import {Receipt, ScanReceiptResponse} from '../../../services/receipt/receiptModel';
import GroupsListSelect from '../GroupsListSelect/GroupsListSelect';
import CaptureImage from './CaptureImage';
import ReceiptReview, {ReceiptReviewDraft, draftRows} from './ReceiptReview';
import ScanProgress, {SCAN_STEPS} from './ScanProgress';
import ScanAnalysisProgress from './ScanAnalysisProgress';
import {RECEIPT_QUOTA_KEY, useReceiptQuota} from './useReceiptQuota';
import {buildCreateRequest, createRow, scanResponseToRows} from './reviewModel';
import classes from './ReceiptCreationFlow.module.scss';
import ReceiptWizardLayout from './ReceiptWizardLayout';
import PracticeHint from '../Layout/PracticeHint';
import LoadingIndicator from '../LoadingIndicator';

type Step = 'setup' | 'capture' | 'scanning' | 'scan-complete' | 'manual-review' | 'scan-review' | 'scan-destination' | 'scan-split';
const MANUAL_STEPS = ['Setup', 'Review', 'Split', 'Done'];

export interface ReceiptPractice {
  image: string;
  scan: ScanReceiptResponse;
  groups: Group[];
  onSplit: (draft: ReceiptReviewDraft) => void;
  onBack: () => void;
}

const ReceiptCreationFlow = ({allowManual = false, practice}: {allowManual?: boolean; practice?: ReceiptPractice}) => {
  const navigate = useNavigate();
  const {user} = useAuth();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('capture');
  const suggestedName = useRef(`Receipt ${new Date().toLocaleDateString()}`);
  const [receiptName, setReceiptName] = useState(suggestedName.current);
  const [group, setGroup] = useState<Group>();
  const [destination, setDestination] = useState<'private' | 'group'>('private');
  const [scan, setScan] = useState<ScanReceiptResponse>();
  const [manualDraft, setManualDraft] = useState<ReceiptReviewDraft>();
  const [scanDraft, setScanDraft] = useState<ReceiptReviewDraft>();
  const [scanError, setScanError] = useState<string>();
  const [previewReady, setPreviewReady] = useState(false);
  const scanning = useRef(false);
  const privateSaving = useRef(false);
  const quotaQuery = useReceiptQuota(!practice);
  const quota = practice?.scan.quota ?? quotaQuery.data;
  const groupsQuery = useQuery<Group[], ErrorResponse>(['groups'], () => api.groups.getAll(), {retry: false, enabled: !practice});
  const groups = practice?.groups ?? groupsQuery.data;
  const selectedGroup = groups?.find(item => group && groupKey(item) === groupKey(group));
  const setupValid = receiptName.trim().length > 0 && receiptName.length <= 60;
  const quotaReady = !!quota && (!!practice || (!quotaQuery.isError && !quotaQuery.isLoading));
  const canScan = quotaReady && (quota.unlimited || (quota.remaining ?? 0) > 0);
  const showingScanProgress = step === 'scanning' || step === 'scan-complete';
  const handleScanComplete = useCallback(() => setStep('scan-review'), []);

  useEffect(() => {
    if (!practice || step !== 'scanning') return;
    const timer = window.setTimeout(() => {
      setScan(practice.scan);
      setReceiptName(practice.scan.merchantName ?? 'Practice dinner');
      setScanDraft(undefined);
      setStep('scan-complete');
      scanning.current = false;
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [practice, step]);

  const scanMutation = useMutation<ScanReceiptResponse & ErrorResponse, ErrorResponse, string>(
    image => api.receipt.scan({image, receiptName}),
    {
      onSuccess: data => {
        if (!data?.scanId) {
          setScanError(data?.message || 'Scanning is not available right now. Please try again.');
          queryClient.invalidateQueries(RECEIPT_QUOTA_KEY);
          setStep('capture');
          return;
        }
        queryClient.setQueryData(RECEIPT_QUOTA_KEY, data.quota);
        setScan(data);
        if (data.merchantName && receiptName === suggestedName.current) setReceiptName(data.merchantName.slice(0, 60));
        setScanDraft(undefined);
        setStep('scan-complete');
      },
      onError: error => {
        queryClient.invalidateQueries(RECEIPT_QUOTA_KEY);
        setScanError(error?.message || 'Scanning failed. Please try again.');
        setStep('capture');
      },
      onSettled: () => { scanning.current = false; },
    }
  );

  const privateSave = useMutation<Receipt, ErrorResponse>(async () => {
    if (practice) throw new Error('Choose the sample group to practise splitting. Nothing is saved in practice.');
    if (!scanDraft || !scan?.scanId || !user) throw new Error('Return to review before saving.');
    const request = buildCreateRequest({
      name: receiptName.trim(), groupName: '', rows: draftRows(scanDraft),
      members: [user as any], mode: 'equal', currency: scan.currency, scanId: scan.scanId,
    });
    const result = await api.receipt.create(request);
    if (!result?.id || !Array.isArray(result.entries)) {
      throw new Error((result as unknown as ErrorResponse)?.message || 'Could not save your receipt. Please try again.');
    }
    return result;
  }, {
    onSuccess: receipt => {
      queryClient.invalidateQueries(['history']);
      queryClient.invalidateQueries(['userStatistics']);
      queryClient.invalidateQueries(['getStatistics']);
      navigate(`/receipt/${receipt.id}`);
    },
    onSettled: () => { privateSaving.current = false; },
  });

  const handleScan = (image: string) => {
    if (!canScan || scanning.current) return;
    scanning.current = true;
    setScanError(undefined);
    setStep('scanning');
    if (!practice) scanMutation.mutate(image);
  };

  const guidance = step === 'scan-destination'
    ? destination === 'private'
      ? {target: '[data-tour="share-group"]', title: 'Private or shared?', content: 'Private keeps the receipt just for you. Share with a group lets you divide it. Choose Share with a group.'}
      : !selectedGroup
        ? {target: '[data-tour="group-card"]', title: 'Choose Dinner friends', content: 'Select this sample group with Alex, Maria and guest Sam.'}
        : {target: '[data-tour="destination-continue"]', title: 'Your group is selected', content: 'Tap Continue to split to assign the items.'}
    : step === 'scan-review'
      ? {target: '[data-tour="review-continue"]', title: 'Check the items', content: 'Check the detected products, quantities and prices. You can edit them. Then tap Looks good to me.'}
      : previewReady
        ? {target: '[data-tour="confirm-photo"]', title: 'Use the sample receipt', content: 'Tap Use this photo to simulate uploading and analyzing it. No real upload or scan charge.'}
        : {target: '[data-tour="take-photo"]', title: 'Take a photo', content: 'Tap Take a photo. We’ll load a sample automatically instead of opening your camera.'};
  const withGuide = (view: React.ReactNode) => <>
    {view}
    {practice && !showingScanProgress && <PracticeHint key={`guide-${guidance.target}`} {...guidance}/>}
  </>;

  if (step === 'scan-destination') {
    return withGuide(<ReceiptWizardLayout tourStage="destination" title="Name & destination" onBack={() => setStep('scan-review')}
      backLabel="Back to review items" backDisabled={privateSave.isLoading}>
      <fieldset className={classes.destinationForm} disabled={privateSave.isLoading}>
        <div className={classes.field}>
          <label htmlFor="destination-name">Receipt name</label>
          <input id="destination-name" value={receiptName} maxLength={60} onChange={event => setReceiptName(event.target.value)}/>
          <p>Pre-filled from receipt — you can change it.</p>
        </div>
        <section className={classes.groups} aria-label="Destination">
          <h2>Destination</h2>
          <div className={classes.destinations}>
            <button type="button" aria-pressed={destination === 'private'} onClick={() => setDestination('private')}>
              <LockOutlined aria-hidden="true"/><strong>Private</strong><span>Just for me</span>
            </button>
            <button data-tour="share-group" type="button" aria-pressed={destination === 'group'} onClick={() => setDestination('group')}>
              <PeopleOutlineRounded aria-hidden="true"/><strong>Share with a group</strong><span>Split with others</span>
            </button>
          </div>
        </section>
        {destination === 'group' && <section className={classes.groups}>
          <h2>Choose a group</h2>
          {!practice && groupsQuery.isLoading ? <LoadingIndicator label="Loading groups"/> : !practice && groupsQuery.isError ?
            <Alert severity="error" action={<Button onClick={() => groupsQuery.refetch()}>Retry groups</Button>}>Could not load your groups.</Alert> :
            groups?.length ? <GroupsListSelect groups={groups} selectedGroupName={group ? groupKey(group) : undefined} updateCurrentGroup={setGroup} variant="creation"/> :
            <p>No groups yet. Keep this receipt private or create a group from Groups.</p>}
        </section>}
        {destination === 'private' && <div className={classes.privateNotice}>
          <ReceiptOutlined aria-hidden="true"/><span>Private receipts are saved for your personal records only.</span>
        </div>}
        {privateSave.isError && <Alert severity="error">{privateSave.error?.message || 'Could not save. Your items are still here.'}</Alert>}
        <div className={classes.actions}><button data-tour="destination-continue" type="button" className={classes.primary}
          disabled={!setupValid || privateSave.isLoading || (!practice && !user) || (destination === 'group' && (!selectedGroup || (!practice && (groupsQuery.isError || groupsQuery.isLoading))))}
          onClick={() => {
            if (privateSaving.current) return;
            if (destination === 'private') {
              privateSaving.current = true;
              privateSave.mutate();
              return;
            }
            const nextGroup = selectedGroup;
            if (practice && scanDraft) {
              practice.onSplit({...scanDraft, name: receiptName});
              return;
            }
            setGroup(nextGroup);
            setScanDraft(previous => previous ? {...previous, name: receiptName,
              mode: 'items',
              rows: nextGroup && previous.groupName === groupKey(nextGroup) ? previous.rows : previous.rows.map(row => ({...row, memberIds: [], allocations: undefined})),
            } : previous);
            setStep('scan-split');
          }}>{privateSave.isLoading ? 'Saving…' : destination === 'group' ? 'Continue to split →' : <><LockOutlined/>Save privately</>}</button></div>
      </fieldset>
    </ReceiptWizardLayout>);
  }

  if (step === 'manual-review' || step === 'scan-review' || step === 'scan-split') {
    const scanned = step !== 'manual-review';
    return withGuide(
      <ReceiptReview
        practice={!!practice}
        key={step}
        stage={scanned ? (step === 'scan-review' ? 'items' : 'split') : undefined}
        onContinue={draft => {
          setScanDraft(draft);
          setReceiptName(draft.name);
          setDestination(group ? 'group' : 'private');
          setStep('scan-destination');
        }}
        initialRows={scanned && scan ? scanResponseToRows(scan) : [createRow()]}
        initialDraft={scanned ? scanDraft : manualDraft}
        initialMode={group?.splitPreference === 'items' ? 'items' : 'equal'}
        receiptName={receiptName}
        groupName={group ? groupKey(group) : ''}
        members={group ? allocationMembers(selectedGroup ?? group) : user ? [user as any] : []}
        guests={(selectedGroup ?? group)?.guests ?? []}
        currency={scanned ? scan?.currency : undefined}
        scanId={scanned ? scan?.scanId : undefined}
        ocrTotal={scanned ? scan?.total : undefined}
        warnings={scanned ? scan?.warnings : undefined}
        onBack={draft => {
          if (scanned) setScanDraft(draft);
          else setManualDraft(draft);
          setReceiptName(draft.name);
          setStep(step === 'scan-split' ? 'scan-destination' : scanned ? 'capture' : 'setup');
        }}
        backLabel={step === 'scan-split' ? 'Back to destination' : scanned ? 'Back to capture' : 'Back to setup'}
        progressSteps={scanned ? SCAN_STEPS : MANUAL_STEPS}
        reviewStepIndex={step === 'scan-split' ? 3 : 1}
      />
    );
  }

  const quotaStatus = practice ? <div className={classes.quota} role="status">Practice scan · No allowance used</div> : quotaQuery.isError ? (
    <Alert severity="error" action={<Button onClick={() => quotaQuery.refetch()} disabled={quotaQuery.isFetching}>Retry quota</Button>}>
      Could not check your scan quota. Please try again.
    </Alert>
  ) : !quotaReady ? (
    <div className={classes.quota}><LoadingIndicator label="Checking your scan quota" variant="inline"/></div>
  ) : (
    <div className={classes.quota} role="status">
      <ReceiptOutlined aria-hidden="true"/>
      <span>{quota.unlimited ? 'Unlimited scans' : `${quota.remaining ?? 0} scans left · Resets ${new Date(quota.resetAt).toLocaleDateString()}`}
        {' · Failed scans don’t count'}
        {!quota.unlimited && (quota.remaining ?? 0) <= 0 && '. Your allowance renews on the date above.'}
      </span>
    </div>
  );

  return withGuide(
    <ReceiptWizardLayout tourStage={step === 'capture' ? 'capture' : undefined} title={showingScanProgress ? 'Scanning receipt' : 'New receipt'}
      contentClassName={showingScanProgress ? classes.scanning : undefined}
      backLabel={!allowManual ? 'Back to home' : step === 'setup' ? 'Back to groups' : 'Back to setup'}
      onBack={showingScanProgress ? undefined : () => {
        if (scanning.current) return;
        if (practice) {practice.onBack(); return;}
        if (!allowManual) navigate('/home');
        else if (step === 'setup') navigate('/groups');
        else setStep('setup');
      }}>
        {showingScanProgress ? (
          <ScanAnalysisProgress complete={step === 'scan-complete'} onComplete={handleScanComplete}/>
        ) : step === 'capture' ? (
          <Stack spacing={2}>
            <ScanProgress activeStep={0}/>
            <Typography variant="h5" component="h2">Scan receipt</Typography>
            {quotaStatus}
            {scanError && <Alert severity="error">{scanError}</Alert>}
            {scan && (
              <>
                <Button variant="contained" onClick={() => setStep('scan-review')}>Return to review</Button>
                <Typography variant="body2">Your previous scan is saved in this draft. Using another photo starts a new scan and counts toward your quota if successful.</Typography>
              </>
            )}
            {allowManual && <Button onClick={() => setStep('setup')}>Add manually</Button>}
            <CaptureImage sampleImage={practice?.image} onPreviewChange={setPreviewReady} onConfirm={handleScan} disabled={!canScan || scanMutation.isLoading}/>
          </Stack>
        ) : (
          <>
            {quotaStatus}
            <div className={classes.field}>
              <label htmlFor="new-receipt-name">Receipt name *</label>
              <input id="new-receipt-name" required maxLength={60} value={receiptName}
                placeholder="e.g. Dinner at Thai Garden" onChange={event => setReceiptName(event.target.value)}/>
            </div>
            <section className={classes.groups} aria-labelledby="choose-group">
              <h2 id="choose-group">Choose a group (optional)</h2>
              <Button onClick={() => setGroup(undefined)} aria-pressed={!group}>Keep private</Button>
              {groupsQuery.isLoading ? <LoadingIndicator label="Loading groups"/> : groupsQuery.isError ? (
                <Alert severity="error" action={<Button onClick={() => groupsQuery.refetch()} disabled={groupsQuery.isFetching}>Retry groups</Button>}>
                  Could not load your groups.
                </Alert>
              ) : groups?.length ? (
                <GroupsListSelect groups={groups} selectedGroupName={selectedGroup ? groupKey(selectedGroup) : undefined}
                  updateCurrentGroup={setGroup} variant="creation"/>
              ) : <p>No groups yet. You can keep this receipt private.</p>}
              {!groupsQuery.isLoading && (!groups?.length || groupsQuery.isError) && (
                <Button component={Link} to="/groups">Create a group</Button>
              )}
            </section>
            <div className={classes.actions}>
              <button type="button" className={classes.primary} disabled={!canScan} onClick={() => {
                setGroup(selectedGroup);
                setStep('capture');
              }}><PhotoCameraOutlined/>Scan receipt</button>
              <button type="button" className={classes.secondary} disabled={!setupValid} onClick={() => {
                setGroup(selectedGroup);
                setStep('manual-review');
              }}><EditOutlined/>Add manually</button>
            </div>
            {scan && <Button fullWidth onClick={() => setStep('scan-review')} disabled={!setupValid}>Return to review</Button>}
          </>
        )}
    </ReceiptWizardLayout>
  );
};

export default ReceiptCreationFlow;
